import {Button, Group, Stack, Text} from '@mantine/core';
import {useCallback, useMemo} from 'react';
import {SubmitHandler, useFormContext} from 'react-hook-form';
import {Link, useNavigate} from 'react-router-dom';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync, useAsyncFn} from 'react-use';
import {FilesService} from '../api/generated/FilesService';
import {RepairDocumentsService} from '../api/generated/RepairDocumentsService';
import {AsyncStateContainer} from '../components/async-state-container';
import {BasicPage} from '../components/basic-page';
import {DeleteButton} from '../components/delete-button';
import {BreadcrumbPart} from '../components/page-title';
import {createForm} from '../forms';
import {useNotification} from '../hooks/use-notifications';
import {ROUTES} from '../routes';
import {repairDocumentSchema, RepairDocumentSchema} from './schemas';
import {extractFileName} from '../forms/adapters/file-button';

const RepairDocumentForm = createForm<RepairDocumentSchema>();

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Repair Documents', href: ROUTES.ADMIN.REPAIR_DOCUMENTS.INDEX.path},
  {title: 'Create'},
];

type RepairDocumentCreateProps = {};

const DEFAULT_VALUES: RepairDocumentSchema = {
  description: '',
  name: '',
  sortOrder: 0,
  url: '',
  fileName: '',
};

export const RepairDocumentCreate: React.FC<RepairDocumentCreateProps> = () => {
  const navigate = useNavigate();
  const notifications = useNotification();

  const onSubmit = useCallback<SubmitHandler<RepairDocumentSchema>>(
    async (value) => {
      if (!value.name) value.name = extractFileName(value.url);
      const response = await RepairDocumentsService.create({body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Repair Document Created');
      navigate(ROUTES.ADMIN.REPAIR_DOCUMENTS.INDEX.path);
    },
    [navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <RepairDocumentForm
        defaultValues={DEFAULT_VALUES}
        schema={repairDocumentSchema}
        onSubmit={onSubmit}
      >
        <RepairDocumentForm.Container>
          <FormFields />
          <Group mt={24} justify={'flex-end'}>
            <Button
              type='button'
              variant='outline'
              component={Link}
              to={ROUTES.ADMIN.REPAIR_DOCUMENTS.INDEX.path}
            >
              Cancel
            </Button>
            <RepairDocumentForm.Submit>
              Create Repair Document
            </RepairDocumentForm.Submit>
          </Group>
        </RepairDocumentForm.Container>
      </RepairDocumentForm>
    </BasicPage>
  );
};

type RepairDocumentUpdateProps = {};

export const RepairDocumentUpdate: React.FC<RepairDocumentUpdateProps> = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.REPAIR_DOCUMENTS.DETAIL);

  const fetchRepairDocument = useAsync(async () => {
    const {data} = await RepairDocumentsService.getById({
      id,
    });
    return data;
  }, [id]);

  const repairDocument = fetchRepairDocument.value || undefined;
  const repairDocumentName = repairDocument?.name ?? repairDocument?.fileName;

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {
        title: 'Repair Document',
        href: ROUTES.ADMIN.REPAIR_DOCUMENTS.INDEX.path,
      },
      {title: repairDocumentName ?? ''},
    ],
    [repairDocumentName],
  );

  const onSubmit = useCallback<SubmitHandler<RepairDocumentSchema>>(
    async (value) => {
      const response = await RepairDocumentsService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Repair Document Updated');
      navigate(ROUTES.ADMIN.REPAIR_DOCUMENTS.INDEX.path);
    },
    [id, navigate, notifications],
  );

  const [deleteDocumentState, deleteDocument] = useAsyncFn(async () => {
    const response = await RepairDocumentsService.deleteById({id});

    if (response.hasErrors) {
      notifications.error('Unable to delete document');
      return;
    }

    notifications.success('Document deleted');
    navigate(ROUTES.ADMIN.REPAIR_DOCUMENTS.INDEX.path);
  }, [id, navigate, notifications]);

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchRepairDocument}>
        <RepairDocumentForm
          onSubmit={onSubmit}
          schema={repairDocumentSchema}
          defaultValues={repairDocument}
        >
          <RepairDocumentForm.Container>
            <FormFields />
            <Group mt={24} justify='space-between'>
              <DeleteButton
                onConfirm={deleteDocument}
                buttonLabel='Delete Document'
                confirmButtonLabel='Delete Document'
                loading={deleteDocumentState.loading}
                confirmTitle={
                  <Text fw={600}>
                    Are you sure you want to delete this document?
                  </Text>
                }
              />
              <Button
                type='button'
                variant='outline'
                component={Link}
                to={ROUTES.ADMIN.REPAIR_DOCUMENTS.INDEX.path}
              >
                Cancel
              </Button>
              <RepairDocumentForm.Submit>
                Update Repair Document
              </RepairDocumentForm.Submit>
            </Group>
          </RepairDocumentForm.Container>
        </RepairDocumentForm>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  const notifications = useNotification();

  const {setValue} = useFormContext<RepairDocumentSchema>();

  const [{loading: urlLoading}, onDrop] = useAsyncFn(
    async (file: File | null) => {
      if (!file) {
        return;
      }
      setValue('fileName', file.name);
      setValue('type', file.type);

      const response = await FilesService.upload({file});
      if (response.hasErrors) {
        notifications.errors(response.errors);
        return;
      }
      notifications.success('File Uploaded');
      return response.data?.uri;
    },
    [notifications, setValue],
  );

  return (
    <>
      <RepairDocumentForm.Row>
        <Stack>
          <Group>
            <Stack style={flexStyle}>
              <RepairDocumentForm.TextInput name='name' label='Name' />
            </Stack>
            <Stack w='25%'>
              <RepairDocumentForm.NumberInput
                name='sortOrder'
                label='Sort Order'
              />
            </Stack>
          </Group>

          <RepairDocumentForm.TextArea
            name='description'
            label='Description'
            placeholder='Description'
          />
        </Stack>
        <Stack>
          <RepairDocumentForm.FileButton
            name='url'
            label='Document'
            loading={urlLoading}
            onChange={onDrop}
          />
        </Stack>
      </RepairDocumentForm.Row>
    </>
  );
};

const flexStyle = {
  flex: 1,
};
